'use strict';

import { BaseElement } from './BaseElement';
import { changeLangToCurrent, getCurrentLangDirection } from '../components/langSwitcher/langSwitcher';
import { log } from 'three/examples/jsm/nodes/math/MathNode';
import { isDesktop, settingsData } from '../../settings';
import {PopUpPage} from "./pages/PopUp";
import {closeAsideMenu} from "../menu/menu";

export class DropDown {
	constructor (parent,className = null,params = {}) {
		this.parent = parent;
		this.className = className;
		this.params = params;
		this.globalHandler = null;
		this.overflow = false;
		this.overflowStopSelector = null;
		this.overflowParent = null;
		this.placeholder = null;
		this.arrowIcon = 'icon-down2';
		this.position = 'dropdown--bottom';
		this.rotateArrow = true;
		this.elements = [];
		this.handler = this.defaultHandler.bind(this);
	}

	create() {
		this.dropDown = new BaseElement()
			.addClass('dropdown',this.#addAdditionalClassName(),this.position, this.rotateArrow ? '--rotate-arrow' : null)
			.event('click',this.handler)
			.appendTo(this.parent)
			.DOMElement;

		const titleBlock = new BaseElement()
			.addClass('dropdown__title-block',this.#addAdditionalClassName('__title-block'))
			.appendTo(this.dropDown)
			.DOMElement;
		
		if (this.params.placeholder) {
			this.placeholder = new BaseElement('span')
				.addClass('dropdown__title','dropdown__placeholder',this.#addAdditionalClassName('__title'))
				.appendTo(titleBlock);
		}

		if (this.params.title) {
			new BaseElement('span')
				.addClass('dropdown__title','language-string',this.#addAdditionalClassName('__title'))
				.addAttribute('data-dictionary',this.params.title)
				.innerHtml(changeLangToCurrent(this.params.title))
				.appendTo(titleBlock)
				.DOMElement;
		}

		if (this.params.subTitle) {
			new BaseElement('span')
				.addClass('dropdown__sub-title','language-string',this.#addAdditionalClassName('__sub-title'))
				.addAttribute('data-dictionary',this.params.subTitle)
				.innerHtml(changeLangToCurrent(this.params.subTitle))
				.appendTo(titleBlock)
				.DOMElement;
		}

		this.arrowBlock = new BaseElement()
			.addClass('dropdown__arrow-block',this.#addAdditionalClassName('__arrow-block'),'hidden')
			.appendTo(this.dropDown)
			.DOMElement;

		new BaseElement('span')
			.addClass('dropdown__separator',this.#addAdditionalClassName('__separator'))
			.appendTo(this.arrowBlock);

		this.arrow = new BaseElement()
			.addClass('dropdown__arrow',this.#addAdditionalClassName('__arrow'),this.arrowIcon)
			.appendTo(this.arrowBlock);

		this.contentWrapper = new BaseElement()
			.addClass('dropdown__content-wrapper',this.#addAdditionalClassName('__content-wrapper'),this.overflow ? '--overflow' : null)
			// .appendTo(this.overflow ? document.body : this.dropDown)
			.DOMElement;
		
		if (!this.overflow) {
			this.dropDown.appendChild(this.contentWrapper);
		} else {
			this.contentWrapper.addEventListener('transitionend',(e) => {
				if (!this.contentWrapper.classList.contains('--show-dropdown-fixed')) {
					this.contentWrapper.remove();
					this.overflowParent = null;
				}
			})
		}

		this.content = new BaseElement()
			.addClass('dropdown__content',this.#addAdditionalClassName('__content'))
			.appendTo(this.contentWrapper);

		this.curtain = new BaseElement()
			.addClass('dropdown__curtain')
			.appendTo(this.content.DOMElement)
			.DOMElement;

		new BaseElement()
			.addClass('dropdown__curtain-line')
			.appendTo(this.curtain);

		return this;
	}
	
	changePlaceholder(value) {
		if (this.placeholder) {
			this.placeholder.innerHtml(value);
		}
	}
	
	findParent() {
		if (this.overflow && !this.overflowParent && this.overflowStopSelector) {
			const parents = [];
			let currentElement = this.parent;
			
			while (currentElement && !currentElement.matches(this.overflowStopSelector)) {
				currentElement = currentElement.parentElement;
				if (currentElement) {
					parents.push(currentElement);
				}
			}
			parents.pop();
			this.overflowParent = parents[parents.length - 1];
			this.overflowParent.appendChild(this.contentWrapper);
		}
	}

	setOpenPosition(position) {
		switch (position) {
			case 'top':
				// this.arrowIcon = 'icon-up2';
				this.position = 'dropdown--top';
				if (this.dropDown) {
					this.dropDown.classList.remove('dropdown--bottom');
					this.dropDown.classList.add(this.position);
				}
				break;
			case 'bottom':
				// this.arrowIcon = 'icon-down2';
				this.position = 'dropdown--bottom';
				if (this.dropDown) {
					this.dropDown.classList.remove('dropdown--top');
					this.dropDown.classList.add(this.position);
				}
				break;
			case 'auto':
				if (this.overflow) {
					const dropDownBounding = this.dropDown.getBoundingClientRect();
					const contentBounding = this.content.DOMElement.getBoundingClientRect();
					const parentBounding = this.overflowParent.getBoundingClientRect();
					
					let x = dropDownBounding.x - parentBounding.x;
					let y = dropDownBounding.y - parentBounding.y;
					let moveX = false;
					
					if (y + contentBounding.height > window.innerHeight) {
						y -= contentBounding.height;
					}
					
					if (x + contentBounding.width + parentBounding.x > window.innerWidth) {
						moveX = true;
						x -= contentBounding.width;
					}
					
					if (!moveX && getCurrentLangDirection() === 'rtl' && x - contentBounding.width > 0) {
						x -= contentBounding.width;
					}
					
					if (isDesktop && this.overflowParent && this.overflowParent.classList.contains('unit-card')) {
						if (getCurrentLangDirection() === 'rtl') {
							x = dropDownBounding.x - parentBounding.x - contentBounding.width + 15;
							y = dropDownBounding.y - parentBounding.y - 30;
						} else {
							x = dropDownBounding.x - parentBounding.x - contentBounding.width - 30;
							y = dropDownBounding.y - parentBounding.y - 30;
						}
					}
					
					this.setOverflowPosition(x,y);
				} else {
					this.dropDown.classList.remove(this.position);
					const contentBounding = this.content.DOMElement.getBoundingClientRect();
					const size = contentBounding.y + contentBounding.height + contentBounding.height / 2;

					if (size > window.innerHeight) {
						this.position = 'dropdown--top';
					} else {
						this.position = 'dropdown--bottom';
					}
					this.dropDown.classList.add(this.position);
				}
				break;
		}

		return this;
	}

	setOverflowPosition(x,y) {
		if (this.overflow) {
			this.contentWrapper.style.left = x + 'px';
			this.contentWrapper.style.top = y + 'px';
		}
	}

	needsArrowRotate(state) {
		this.rotateArrow = state;

		return this;
	}

	#addAdditionalClassName(suffix = '') {
		if (this.className) {
			return this.className + suffix;
		}
		return null;
	}

	#globalCloseHandler(e) {
		if (this.overflow) {
			if (this.contentWrapper.classList.contains('--show-dropdown-fixed')) {
				this.hide();
			}
		} else {
			// if (e.target.getAttribute('data-menu-item') === 'contactUs') {
			// 	return;
			// }

			if (
				(!e.target.closest('.dropdown') && this.dropDown.classList.contains('--show-dropdown')) ||
				(e.target.classList.contains('dropdown__content-wrapper') && this.dropDown.classList.contains('--show-dropdown')))
			{
				this.hide();
			}
		}
	}

	hide() {
		if (this.dropDown) {
			if (this.overflow) {
				this.contentWrapper.classList.remove('--show-dropdown-fixed');
			} else {
				this.dropDown.classList.remove('--show-dropdown');
			}
			if (this.globalHandler) {
				window.removeEventListener('pointerdown',this.globalHandler,true);
				this.globalHandler = null;
			}
		}
	}

	show() {
		if (this.dropDown) {
			if (this.globalHandler) {
				this.hide();
			}

			if (this.overflow) {
				this.contentWrapper.classList.add('--show-dropdown-fixed');
			} else {
				this.dropDown.classList.add('--show-dropdown');
			}

			this.globalHandler = this.#globalCloseHandler.bind(this);
			window.addEventListener('pointerdown',this.globalHandler,true);
		}
		
		// PopUpPage.hide();
	}

	setCustomHandler(handlerFunc) {
		this.handler = handlerFunc;

		return this;
	}

	defaultHandler(e) {
		e.preventDefault();

		if (this.elements.length === 1) {
			this.elements[0].click();
			return;
		}

		if (!e.target.classList.contains('dropdown__content-element')) {
			if (this.overflow) {
				this.contentWrapper.classList.toggle('--show-dropdown-fixed');
			} else {
				this.dropDown.classList.toggle('--show-dropdown');
			}

			if (this.globalHandler) {
				this.hide();
			} else {
				this.globalHandler = this.#globalCloseHandler.bind(this);
				window.addEventListener('pointerdown',this.globalHandler,true);
			}

			// PopUpPage.hide();
		} else {
			this.hide();
		}
	}

	add(element) {
		if (this.content && element) {
			element.classList.add('dropdown__content-element',this.#addAdditionalClassName('__content-element'));
			this.content.appendElement(element);
			this.elements.push(element);
		}

		if (this.elements.length > 1) {
			this.arrowBlock.classList.remove('hidden');
		}

		return this;
	}

}
